import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, RotateCcw, X } from 'lucide-react'
import { EYEBROW } from '../cc/tokens'
import { ago } from '../lib/format'
import { EIM_STATUS_STYLE } from './statusStyle'
import {
  formatIdlePowerW, GTI_LOW_SOC_PCT, idleMinutes, overheatMinutes,
  type EimAlertAck, type EimAlertKind, type EimVehicle,
} from './useEimApi'

/** Ultratech's own AlertDetail.tsx pattern (TriagePill + Action buttons), ported to EIM's
 *  simpler two-state model -- there is no "resolved" here because an idle/low-battery
 *  alert isn't a persisted incident with a lifecycle of its own the way a breakdown is:
 *  it's a live read off telemetry (isIdleAlert/isLowSoc in useEimApi.ts) that clears itself
 *  the moment the vehicle moves or charges back up. Acknowledged is the only state worth
 *  recording, which is exactly what eim_alert_ack persists.
 *
 *  No "call driver" action, unlike Ultratech's own AlertDetail -- EIM's vendor feed carries
 *  no driver name or phone number anywhere (see EimVehicle in useEimApi.ts), so there is
 *  nothing honest to wire that button to here. */

const SITE_LABEL: Record<EimVehicle['site'], string> = { gti: 'GTI', jm_baxi: 'JM Baxi' }

export function EimAlertDetail({
  v, kind, ack, onClose, onAck, onUnack,
}: {
  v: EimVehicle
  kind: EimAlertKind
  ack: EimAlertAck | null
  onClose: () => void
  onAck: () => Promise<void>
  onUnack: () => Promise<void>
}): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const first = useRef<HTMLButtonElement>(null)

  useEffect(() => { first.current?.focus() }, [])
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])

  const style = kind === 'IDLE_ALERT' ? EIM_STATUS_STYLE.alert
    : kind === 'BATTERY_OVERHEAT' ? EIM_STATUS_STYLE.overheat
    : EIM_STATUS_STYLE.breakdown
  const label = kind === 'IDLE_ALERT' ? 'idle alert' : kind === 'BATTERY_OVERHEAT' ? 'battery overheat' : 'low battery'

  async function act(fn: () => Promise<void>): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not reach the server')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[rgba(15,23,42,0.28)] p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label={`${label} for ${v.vehicle_id}`}
           className="w-full max-w-[380px] animate-slide-up rounded-xl border border-[rgba(15,23,42,0.10)]
                      bg-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.55)]">
        <header className="flex items-center gap-2.5 border-b border-[rgba(15,23,42,0.07)] px-4 py-3">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg" style={{ backgroundColor: style.bg }}>
            <AlertTriangle size={15} style={{ color: style.text }} />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[13px] font-semibold text-[#0F172A]">{v.vehicle_id}</div>
            <div className={EYEBROW}>{SITE_LABEL[v.site]} · {label}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="ml-auto rounded p-1 text-[#8794A7] hover:bg-[rgba(15,23,42,0.05)]">
            <X size={15} />
          </button>
        </header>

        <div className="flex flex-col gap-3 px-4 py-3.5">
          {kind === 'IDLE_ALERT' ? (
            <div>
              <div className={EYEBROW}>idling</div>
              <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-[#0F172A]">
                {idleMinutes(v, Date.now())}m
              </div>
              {v.idle_power_w != null && (
                <div className="mt-0.5 text-[12px] tabular-nums text-[#64748B]">
                  {formatIdlePowerW(v.idle_power_w)} draw · {v.soc != null ? `${v.soc.toFixed(0)}% SOC` : '—'}
                </div>
              )}
            </div>
          ) : kind === 'BATTERY_OVERHEAT' ? (
            <div>
              <div className={EYEBROW}>max cell temperature</div>
              <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-[#9A3412]">
                {v.max_cell_temp != null ? `${v.max_cell_temp.toFixed(1)}°C` : '—'}
              </div>
              <div className="mt-0.5 text-[12px] tabular-nums text-[#64748B]">
                sustained {overheatMinutes(v, Date.now())}m
              </div>
            </div>
          ) : (
            <div>
              <div className={EYEBROW}>state of charge</div>
              <div className="mt-0.5 text-[22px] font-semibold tabular-nums text-[#9F1239]">
                {v.soc != null ? `${v.soc.toFixed(0)}%` : '—'}
              </div>
              <div className="mt-0.5 text-[12px] text-[#64748B]">below the {GTI_LOW_SOC_PCT}% GTI floor</div>
            </div>
          )}

          {ack ? (
            <div className="rounded-md bg-[rgba(245,158,11,0.10)] px-2.5 py-1.5 text-[11px] text-[#B45309]">
              acknowledged by {ack.by_whom} · {ago(ack.acked_ts)} ago
            </div>
          ) : (
            <div className="rounded-md bg-[rgba(15,23,42,0.04)] px-2.5 py-1.5 text-[11px] text-[#64748B]">
              not yet acknowledged
            </div>
          )}

          {error && <div className="text-[11px] text-[#9F1239]">{error}</div>}

          <div className="flex gap-2">
            {ack ? (
              <button
                ref={first}
                type="button"
                disabled={busy}
                onClick={() => void act(onUnack)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[rgba(15,23,42,0.10)]
                           px-3 py-2 text-[12px] font-semibold text-[#64748B] transition-colors
                           hover:border-[rgba(15,23,42,0.18)] hover:text-[#0F172A] disabled:opacity-50"
              >
                <RotateCcw size={13} /> {busy ? 'Reopening…' : 'Reopen'}
              </button>
            ) : (
              <button
                ref={first}
                type="button"
                disabled={busy}
                onClick={() => void act(onAck)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[rgba(16,185,129,0.32)]
                           bg-[rgba(16,185,129,0.10)] px-3 py-2 text-[12px] font-semibold text-[#047857]
                           transition-colors hover:bg-[rgba(16,185,129,0.18)] disabled:opacity-50"
              >
                <Check size={13} /> {busy ? 'Acknowledging…' : 'Acknowledge'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
